import PropTypes from "prop-types";
import React, { useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import S3FileBrowser from "../../../Browsers/S3FileBrowser";
import AzureFileBrowser from "../../../Browsers/AzureFileBrowser";
import MappingPopup from "./MappingPopup";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import { generateNewCounterName, setPrevState } from "../../../../../util/common";
import { regexName, regexDelimiters, regexFilePath } from "../../../../../util/regularExpress";

import WorkspaceService from "../../../../../service/workspace";

import { DATA_SOURCE } from "../../../../../util/Data/DataModeling";
import { SKIP_FILE_CUSTOM_VALUE_LIMIT } from "../../../../../util/Data/DataLoad";
import { checkReservedKeywords } from "../../../../../util/restrictedStatement";

const useStyles = makeStyles()({
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },

  Panel: {
    padding: "10px 25px",
    backgroundColor: "#FFFFFF",
    marginTop: 0,
    borderRadius: 10,
    boxShadow: "none",
  },
  PanelRow: {
    padding: "12px 0",
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  criticalMessage: {
    marginLeft: "5px",
  },
  subCriticalMessage: {
    marginTop: "8px",
    marginLeft: "5px",
  },
  resetFiles: {
    paddingTop: "0px !important",
    marginTop: "-24px",
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    display: "inline-block",
  },
  Alert: {
    paddingTop: 0,
    paddingBottom: 0,
  },
});

const Types = [
  {
    value: "tracked",
    text: "Tracked Dataset History",
    message:
      "Tracking enabled: This dataset stores all historical changes. Ideal for audits or when tracking data evolution over time. Ensure each row has a unique key.",
  },
  {
    value: "untracked",
    text: "Do Not Track Dataset History",
    message:
      "Latest data only: This dataset keeps just the most recent version of your source data. Good for current-state analysis. No unique key required for each row.",
  },
  {
    value: "readonly",
    text: "Read-Only Data View",
    message:
      "View-only access: This setup doesn’t store or track any data. Best for very large datasets where performance is key. Suitable for quick reference with minimal storage use. No unique key required for each row.",
  },
];

const Confirm = ({ title, description, handleCancel, handleClick, loading }) => {
  return (
    <ConfirmPopup
      configData={{
        popupTitle: title,
        popupMessage: description,
        handleCancel,
        handleClick,
        loadingForConfirm: loading,
      }}
    />
  );
};

const suggestUniqueName = (sources, entities, displayName, tableName) => {
  // Generate unique names only if necessary
  const uniqueTableName = generateNewCounterName(
    [...(sources || []), ...(entities || [])].map((object) => object["name"]?.toLowerCase()),
    tableName,
    "_"
  );

  const uniqueDisplayName = generateNewCounterName(
    (sources || []).map((source) => source["displayName"]?.toLowerCase()),
    displayName,
    " "
  );

  return { displayName: uniqueDisplayName, currentName: uniqueTableName, name: uniqueTableName };
};

const validateDatasetName = (displayname, tableName) => {
  let errors = { nameError: "", displayNameError: "" };
  if (checkReservedKeywords(tableName)) errors.nameError = "Reserved keyword.";
  else if (tableName.length > 50 || tableName.length < 2)
    errors.nameError = "System name must be between 2 to 50 characters.";
  if (displayname.trim().length > 50 || displayname.trim().length < 2)
    errors.displayNameError = "Display name must be between 2 to 50 characters.";

  return errors;
};

InfoSection.propTypes = {
  hasDVKey: PropTypes.bool,
};

function InfoSection({
  nameRef,
  dsInfoState,
  restrictedObject,
  sfErrors,
  setRestrictedObject,
  setSfErrors,
  setDSInfoState,
  setDeleteState,
  loadSnowflakeTables,
  fieldsLength,
  addAllFields,
  IsDataVaultEnabled,
  accessRole,
  hasDVKey,
  sources,
  entities,
}) {
  const { classes } = useStyles();
  const { Workspace_id, dataSourceId } = useParams();
  const inEditMode = useMemo(() => dataSourceId !== "add", [dataSourceId]);

  const config = useSelector((state) => state.config);
  const defaultSourceValues = config.defaultSourceValues;
  const {
    compression: defaultCompression,
    type: defaultFileType,
    delimiter: defaultDelimiter,
    encoding: defaultEncoding,
    qualifier: defaultFieldEnclosedBy,
    errorAction: defaultErrorAction,
  } = defaultSourceValues;

  const {
    delimiters,
    compressionFormats,
    enclosedByOptions,
    encodingTypes,
    fileTypes,
    attributeDataTypes,
    errorActions,
    nullValues,
    previewCount = 100,
  } = config;

  const [bucket, setBucket] = useState("");
  const [showMapping, setShowMapping] = useState(false);
  const [openFileBrowser, setOpenFileBrowser] = useState(false);

  const {
    locationList,
    delimiter,
    fieldEnclosedBy,
    encoding,
    type,
    hasHeaderRow,
    stage,
    location,
    path,
    schema,
    displayName,
    displayNameError,
    nameError,
    currentName,
    schemas,
    tables,
    table,
    shareWithDC,
    excludeDQMCritical,
    removeMissingDataOnLoad,
    datasetType,
    description,
    resetFiles,
    customDelimeter,
    compressionFormat,
    onError,
    onErrorNumber,
    skipFileError,
    nullValue,
    customNullValue,
  } = dsInfoState;

  const [loadTableFields, setLoadTableFields] = useState({
    open: false,
    schemaName: "",
    selectedTable: { TABLE_NAME: "", TABLE_TYPE: "" },
    loadingForConfirm: false,
    updateReadOnly: false,
  });

  const [loadFileFields, setLoadFileFields] = useState({
    open: false,
    handleClick: () => {},
  });

  const { options, formats } = useMemo(() => {
    const options = {};
    const formats = {};

    attributeDataTypes.forEach((option) => {
      if (option.options) {
        if (option.type !== "Date" && option.type !== "DateTime" && option.type !== "Time")
          options[option.type] = option.options;
        else formats[option.type] = option.options;
      }
    });

    return { options, formats };
  }, [attributeDataTypes]);

  const generateNames = useCallback(
    (filepath = "", isCompressionGZ = false, isTabel = false) => {
      // Get file base name
      let filteredName = isTabel ? filepath : filepath.split("/").pop();
      // Remove extension
      if (!isTabel) filteredName = filteredName.slice(0, filteredName.lastIndexOf("."));
      // Remove scond extension in case compression is GZ
      if (isCompressionGZ) filteredName = filteredName.slice(0, filteredName.lastIndexOf("."));

      // Filter system name
      let systemName = filteredName
        .replace(/\s+/g, "_")
        .replace(/\W/g, "")
        .replace(/^[^a-zA-Z]+/, "");

      // Validate names
      const { displayNameError, nameError } = validateDatasetName(filteredName, systemName);

      filteredName = displayNameError ? "" : filteredName;
      systemName = nameError ? "" : systemName;
      let uniqueNames = {};

      uniqueNames = suggestUniqueName(sources, entities, filteredName, systemName);

      return uniqueNames;
    },
    [sources, entities]
  );

  const getLengthFormat = useCallback(
    (type, size) => {
      let length = "";

      if (options[type]?.length) {
        let optionIndex = options[type].length - 1;

        if (size !== "") {
          const foundOption = options?.[type]?.findIndex((item) => {
            if (type === "Char" || type === "String") return item.value >= parseInt(size);
            return item.value === size;
          });

          optionIndex = foundOption !== -1 ? foundOption : optionIndex;
        } else if (size === "" && type === "Number") {
          optionIndex = 0;
        } else if (size === "" && type === "String") {
          optionIndex = 5;
        }

        length = options[type][optionIndex]?.value || "";
      }

      let format = formats[type] ? formats[type]?.[0]?.value : "";

      return { length, format };
    },
    [formats, options]
  );

  const loadFields = useCallback(
    async (schemaName, selectedTable, fromConfirmation = false) => {
      if (fromConfirmation) setPrevState(setLoadTableFields, { loadingForConfirm: true });
      else setPrevState(setDSInfoState, { loading: true });
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.loadSnowflakeFields(Workspace_id, {
        schemaName,
        tableName: selectedTable?.TABLE_NAME,
      });

      if (success && data) {
        const fieldsArr = data.map(({ name, type, size }) => {
          const { length, format } = getLengthFormat(type, size);
          return {
            name,
            type,
            description: "",
            length,
            format,
            customFormat: "",
            primary_attribute: false,
            secondary_attribute: false,
            dataVaultKey: false,
            derivedBy: "",
            derivedValue: "",
            dqm: {
              required: false,
            },
            entity_mapping: {},
            isDeleted: "",
            isDerived: false,
            referenceFrom: {},
            required: true,
          };
        });
        addAllFields(fieldsArr);

        setPrevState(setDSInfoState, { table: selectedTable });
        if (sfErrors.table) setPrevState(setSfErrors, { table: false });
      }

      // Generate Unique display name and system name using table name
      let datasetNames = {};
      if (!inEditMode) {
        datasetNames = generateNames(selectedTable?.TABLE_NAME, false, true);
        setPrevState(setDSInfoState, { ...datasetNames });
      }

      if (fromConfirmation) {
        setLoadTableFields((prev) => {
          const update = { ...prev, loadingForConfirm: false, open: false, updateReadOnly: false };
          if (prev.updateReadOnly && typeof prev.callback === "function") {
            prev.callback();
          }
          return update;
        });
      } else setPrevState(setDSInfoState, { loading: false });
    },
    [
      Workspace_id,
      addAllFields,
      inEditMode,
      generateNames,
      getLengthFormat,
      setDSInfoState,
      setSfErrors,
      sfErrors.table,
    ]
  );

  const handleName = useCallback(
    (str, dataState) => {
      const isValid = regexName(str, "alphaWithUnderscore");
      if (isValid) setPrevState(setDSInfoState, { [dataState]: str });
    },
    [setDSInfoState]
  );

  const handleCustomeDelimeter = useCallback(
    (str, key) => {
      const isValid = regexDelimiters(str);
      if (isValid) setPrevState(setDSInfoState, { [key]: str });
    },
    [setDSInfoState]
  );

  // For reseting data that if connection is changed from cloud to snowflake
  const resetCloudFields = useCallback(() => {
    setPrevState(setDSInfoState, {
      type: defaultFileType,
      customDelimeter: "",
      delimiter: defaultDelimiter,
      fieldEnclosedBy: defaultFieldEnclosedBy,
      encoding: defaultEncoding,
      compressionFormat: defaultCompression,
      hasHeaderRow: "yes",
      path: "",
      connectionType: "",
      dataProvider: "S3",
      onError: defaultErrorAction,
      onErrorNumber: 0,
    });
  }, [
    defaultCompression,
    defaultDelimiter,
    defaultEncoding,
    defaultErrorAction,
    defaultFieldEnclosedBy,
    defaultFileType,
    setDSInfoState,
  ]);

  const handleDSRename = useCallback(() => {
    setDSInfoState((prev) => ({ ...prev, name: prev.currentName }));
    setDeleteState((prev) => ({ ...prev, openConfirm: false, handleCancel: undefined }));
  }, [setDSInfoState, setDeleteState]);

  const handleRenameCancel = useCallback(() => {
    setDSInfoState((prev) => ({ ...prev, currentName: prev.name }));
    setDeleteState((prev) => ({ ...prev, openConfirm: false, handleCancel: undefined }));
  }, [setDSInfoState, setDeleteState]);

  const updateDSInfoStateValue = useCallback(
    (key, value) => {
      setPrevState(setDSInfoState, { [key]: value });
    },
    [setDSInfoState]
  );

  const handleConnectionChange = useCallback(
    (e) => {
      if (location === "SNOWFLAKE") {
        resetCloudFields();
      } else {
        setPrevState(setDSInfoState, {
          schema: "",
          table: { TABLE_TYPE: "", TABLE_NAME: "" },
          tables: [],
        });
      }
      setRestrictedObject({
        table: {},
        schema: "",
      });
      setSfErrors({
        table: false,
        schema: false,
      });
      setPrevState(setDSInfoState, {
        location: e.target.value,
        type: defaultFileType,
      });
    },
    [location, defaultFileType, resetCloudFields, setDSInfoState, setRestrictedObject, setSfErrors]
  );

  const handleSchemaChange = useCallback(
    async (e) => {
      setPrevState(setDSInfoState, {
        schema: e.target.value,
        table: { TABLE_TYPE: "", TABLE_NAME: "" },
      });
      setPrevState(setSfErrors, {
        schema: false,
        table: false,
      });
      await loadSnowflakeTables(e.target.value);
    },
    [loadSnowflakeTables, setDSInfoState, setSfErrors]
  );

  const handleTableChange = useCallback(
    (e, selectedObj, callback) => {
      let selectedTable = { TABLE_NAME: "", TABLE_TYPE: "" };
      if (selectedObj) selectedTable = selectedObj;

      if (selectedObj) {
        if (fieldsLength !== 0)
          setPrevState(setLoadTableFields, {
            open: true,
            schemaName: schema,
            selectedTable,
            updateReadOnly: typeof callback === "function",
            callback,
          });
        else loadFields(schema, selectedTable);
      }
    },
    [fieldsLength, schema, loadFields]
  );

  const handlePathChange = useCallback(
    (e) => {
      if (e.target.value === "") setPrevState(setDSInfoState, { path: "" });
      else if (regexFilePath(e.target.value, true)) {
        setPrevState(setDSInfoState, {
          path: e.target.value,
        });
      }
    },
    [setDSInfoState]
  );

  const handleBrowseClick = useCallback(() => {
    const locationDetail = locationList.find((locationDetail) => locationDetail.Name === location);

    if (locationDetail) {
      const Detail = locationDetail.Detail;

      if (Detail.type.toLowerCase() === "s3" || Detail.type.toLowerCase() === "azure") {
        setOpenFileBrowser(true);
        setBucket(Detail.url);
      }
    }
  }, [location, locationList]);

  const handleDelimiter = useCallback(
    (e) => {
      if (e.target.value !== "custom") setPrevState(setDSInfoState, { customDelimeter: "" });
      setPrevState(setDSInfoState, { delimiter: e.target.value });
    },
    [setDSInfoState]
  );

  const handleOnError = useCallback(
    (e) => {
      setPrevState(setDSInfoState, { onError: e.target.value, onErrorNumber: 0 });
    },
    [setDSInfoState]
  );

  const handleSkipFileValueChange = useCallback(
    (e) => {
      if (regexName(e.target.value, "numeric")) {
        const newVal = Number(e.target.value);
        let skilFileErrorMessage = "";
        if (newVal < SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].min || newVal > SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].max)
          skilFileErrorMessage = `Threshold value should be between ${SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].min} to ${SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].max}.`;

        setPrevState(setDSInfoState, { onErrorNumber: newVal + "", skipFileError: skilFileErrorMessage });
      }
    },
    [onError, setDSInfoState]
  );

  const handleNullValues = useCallback(
    (e) => {
      if (e.target.value !== "custom") setPrevState(setDSInfoState, { customNullValue: "" });
      setPrevState(setDSInfoState, { nullValue: e.target.value });
    },
    [setDSInfoState]
  );

  const handleCustomNullValue = useCallback(
    (customNullValue) => setPrevState(setDSInfoState, { customNullValue }),
    [setDSInfoState]
  );

  const tableNameBlur = useCallback(
    (e) => {
      if (!inEditMode) {
        setDSInfoState((prev) => ({ ...prev, name: prev.currentName }));
      } else if (dsInfoState.name !== e.target.value) {
        setPrevState(setDeleteState, {
          openConfirm: true,
          confirmTitle: "Rename the system name",
          confirmMessage: (
            <span className={classes.deleteReffConfirm}>
              <span>{`Are you sure you want to rename this ${DATA_SOURCE.singular} system name?`}</span>
              <div style={{ marginTop: "16px", display: "flex" }}>
                <span>
                  <ReportProblemOutlinedIcon sx={{ fontSize: "2rem", color: "#D32F2F" }} />
                </span>
                <div>
                  <span className={classes.criticalMessage} style={{ color: "#D32F2F" }}>
                    This will cause changes to the live and historical data tables and records in the data warehouse. If
                    you have any custom-defined procedures or jobs or reports that refer to the old system names, they
                    would need to be updated manually following this step. Only perform this step after performing a
                    thorough impact analysis!
                  </span>
                  <span className={classes.subCriticalMessage}>
                    {`If you simply intend to change the display name of your ${DATA_SOURCE.singular}, cancel this prompt and update
                    the Display Name field instead.`}
                  </span>
                  <span className={classes.subCriticalMessage}>
                    Click `Cancel` to close this prompt without making any changes or Rename to proceed with the
                    renaming. Note that renaming can take significant time and cannot be interrupted.
                  </span>
                </div>
              </div>
            </span>
          ),
          handleClick: () => handleDSRename(),
          handleCancel: () => handleRenameCancel(),
          confirmBtnName: "Rename",
          confirmBtnColor: "error",
        });
      }
    },
    [
      classes.criticalMessage,
      classes.deleteReffConfirm,
      classes.subCriticalMessage,
      inEditMode,
      dsInfoState.name,
      handleDSRename,
      handleRenameCancel,
      setDSInfoState,
      setDeleteState,
    ]
  );

  const onPathBlur = useCallback(() => {
    if (path && path[0] !== "/") setPrevState(setDSInfoState, { path: "/" + path });
  }, [path, setDSInfoState]);

  const handleLoadFields = useCallback(
    (columns) => {
      if (fieldsLength > 0) {
        setLoadFileFields({
          open: true,
          handleClick: () => {
            addAllFields(
              columns.map((mapping) => {
                return {
                  name: mapping.destination.trim(),
                  type: mapping.type,
                  description: "",
                  length: mapping.size ? mapping.size : "",
                  format: mapping.format,
                  customFormat: mapping.customFormat,
                  primary_attribute: false,
                  secondary_attribute: false,
                  entity_mapping: {},
                  dqm: { required: false },
                  required: true,
                };
              })
            );
            setLoadFileFields({ open: false, handleClick: () => {} });
          },
        });
      } else {
        addAllFields(
          columns.map((mapping) => {
            return {
              name: mapping.destination.trim(),
              type: mapping.type,
              description: "",
              length: mapping.size ? mapping.size : "",
              format: mapping.format,
              customFormat: mapping.customFormat,
              primary_attribute: false,
              secondary_attribute: false,
              entity_mapping: {},
              dqm: { required: false },
              required: true,
            };
          })
        );
      }
    },
    [fieldsLength, addAllFields]
  );

  const getS3FileColumns = useCallback(
    async (needFileProperties = true, newPath = "") => {
      setPrevState(setDSInfoState, { loading: true });
      const workspace = new WorkspaceService();

      let bucketName = "";
      let file = "";
      let tempPath = newPath ? newPath : path;

      const location = locationList?.find((loc) => loc.Stage_id === stage);

      if (dsInfoState.connectionType.toLowerCase() === "s3") {
        const bucket = location?.Detail?.url;

        //Setting default values based on url props
        const bucketNameArray = bucket.replace("s3://", "").split("/");
        bucketName = bucketNameArray[0];
        bucketNameArray.shift();
        file = bucketNameArray.length ? bucketNameArray.join("/") : "";
        if (bucketNameArray.length && file && !file.endsWith("/")) file += "/";
        //Checking if path has file selected
        if (tempPath) {
          if (file.endsWith("/")) file = file + tempPath.substring(1);
          else file = file + tempPath;
        }
      } else file = tempPath;

      const response = await workspace.loadFileColumns(Workspace_id, {
        bucket: bucketName,
        file,
        delimiter: delimiter === "custom" ? customDelimeter : delimiter,
        fieldEnclosedBy,
        encoding: encodingTypes.find((en) => en.value === encoding).text ?? "UTF-8", //provide text,
        hasHeaderRow: hasHeaderRow === "yes",
        location: stage,
        fileType: type,
        previewCount,
        needFileProperties,
      });

      setPrevState(setDSInfoState, { loading: false });

      if (response.data) response.data = JSON.parse(response.data);
      return response;
    },
    [
      setDSInfoState,
      path,
      locationList,
      dsInfoState.connectionType,
      Workspace_id,
      delimiter,
      customDelimeter,
      fieldEnclosedBy,
      encodingTypes,
      hasHeaderRow,
      stage,
      type,
      previewCount,
      encoding,
    ]
  );

  const setPath = useCallback(
    async (pathValue) => {
      const tempPath =
        dsInfoState.connectionType.toLowerCase() === "azure"
          ? pathValue.replace(`/${dsInfoState.container}`, "")
          : pathValue;
      setPrevState(setDSInfoState, { path: tempPath });

      const fileData = await getS3FileColumns(true, tempPath);
      if (fileData) {
        let data = fileData.data;

        let datasetNames = {};
        if (!inEditMode) datasetNames = generateNames(data?.fileName, data?.fileProperties?.isCompressionGZ);

        setPrevState(setDSInfoState, {
          delimiter: delimiters.find((d) => d.value === data?.fileProperties?.delimiter)?.value ?? delimiter,
          encoding:
            encodingTypes.find((en) => en.text.toLowerCase() === data?.fileProperties?.encoding?.toLowerCase())
              ?.value ?? encoding,
          fieldEnclosedBy:
            enclosedByOptions.find((q) => q.value === data?.fileProperties?.textQualifier)?.value ?? fieldEnclosedBy,
          ...(data?.fileProperties?.nullValue
            ? { nullValue: "custom", customNullValue: data?.fileProperties?.nullValue }
            : { nullValue: "default", customNullValue: "" }),
          type: fileTypes.find((f) => f.value === data?.fileProperties?.fileType)?.value ?? type,
          hasHeaderRow: data?.fileProperties?.hasHeaderRow ? "yes" : "no",
          ...datasetNames,
        });
      }
    },
    [
      delimiter,
      delimiters,
      dsInfoState.connectionType,
      dsInfoState.container,
      enclosedByOptions,
      encoding,
      encodingTypes,
      fieldEnclosedBy,
      fileTypes,
      getS3FileColumns,
      setDSInfoState,
      type,
      generateNames,
      inEditMode,
    ]
  );

  return (
    <Box className={classes.Panel}>
      <Box maxWidth="xl">
        <Grid container className={classes.PanelRow} spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Dataset Type</InputLabel>
              <Select
                value={dsInfoState.datasetType}
                label="Dataset Type"
                onChange={(e) => setPrevState(setDSInfoState, { datasetType: e.target.value })}
                MenuProps={{ sx: { height: "220px" } }}
              >
                {Types.map((type) => (
                  <MenuItem
                    key={type.value}
                    value={type.value}
                    disabled={
                      (inEditMode && datasetType === "readonly") ||
                      (inEditMode && type.value === "readonly") ||
                      (!inEditMode && location ? type.value === "readonly" && location !== "SNOWFLAKE" : false)
                    }
                  >
                    {type.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Box className={classes.HeaderTitle} sx={{ display: "inline-flex", alignItems: "end" }}>
              <Alert
                className={classes.Alert}
                variant="outlined"
                icon={<InfoOutlinedIcon fontSize="inherit" />}
                severity="info"
              >
                {Types.find((ds) => ds.value === dsInfoState.datasetType).message}
              </Alert>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Connection</InputLabel>
              <Select
                value={location}
                label="Connection"
                onChange={handleConnectionChange}
                MenuProps={{ sx: { height: "220px" } }}
              >
                {locationList.map((option) => (
                  <MenuItem
                    key={option.Stage_id}
                    value={option.Name}
                    disabled={datasetType === "readonly" && option.Stage_id !== "SNOWFLAKE"}
                  >
                    {option.Stage_id === "SNOWFLAKE" ? option.label : option.Name}
                    {option?.IsDataLake === 1 &&
                      (option?.Detail.type.toLowerCase() === "s3" ? (
                        <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                      ) : (
                        <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                      ))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {location === "SNOWFLAKE" ? (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl variant="outlined" fullWidth required error={sfErrors.schema}>
                  <InputLabel>Input Schema</InputLabel>
                  <Select
                    value={schemas.length ? schema : ""}
                    label="Input Schema"
                    onChange={handleSchemaChange}
                    disabled={location !== "SNOWFLAKE"}
                    error={sfErrors.schema}
                  >
                    {inEditMode && location === "SNOWFLAKE" && restrictedObject.schema.length > 0 && (
                      <MenuItem
                        key={restrictedObject.schema}
                        value={restrictedObject.schema}
                        sx={{
                          display: "none",
                        }}
                      >
                        ********
                      </MenuItem>
                    )}
                    {schemas.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {sfErrors.schema && <FormHelperText>Previously selected schema no longer exists</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                {!sfErrors.table && schema !== "" && restrictedObject.schema === schema ? (
                  <TextField disabled value="********" fullWidth label="Table / View" required />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={tables}
                    getOptionLabel={(option) => option.TABLE_NAME}
                    renderInput={(params) => (
                      <TextField
                        variant="outlined"
                        {...params}
                        label="Input Table / View"
                        required
                        error={sfErrors.table}
                        helperText={sfErrors.table ? "Previously selected table/view no longer exists" : ""}
                      />
                    )}
                    onChange={(e, selectedObj) => handleTableChange(e, selectedObj)}
                    disabled={schema.length === 0 || restrictedObject.schema === schema}
                    value={
                      tables.length > 0 &&
                      tables.some(
                        ({ TABLE_NAME, TABLE_TYPE }) =>
                          table.TABLE_NAME === TABLE_NAME && table.TABLE_TYPE === TABLE_TYPE
                      )
                        ? table
                        : { TABLE_NAME: "", TABLE_TYPE: "" }
                    }
                    groupBy={(option) => option.TABLE_TYPE}
                    clearIcon={false}
                  />
                )}
              </Grid>
            </>
          ) : (
            <>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <TextField
                  variant="outlined"
                  value={path}
                  inputProps={{ maxLength: 255 }}
                  onChange={handlePathChange}
                  onBlur={onPathBlur}
                  helperText="Allowed characters 'A-Z','a-z','0-9','/:.-_' and wildcard(*)"
                  fullWidth
                  label="Path"
                  sx={{ mr: 2, flex: 1 }}
                />
              </Grid>
              <Grid
                style={{ display: "flex", paddingTop: "0px" }}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ButtonUI
                  disabled={!(location && type)}
                  variant="contained"
                  color="secondary"
                  endIcon={<UploadFileIcon />}
                  style={{ alignSelf: "center", width: "150px" }}
                  onClick={handleBrowseClick}
                >
                  <span style={{ marginTop: "2px" }}>Browse</span>
                </ButtonUI>
              </Grid>
              <Grid sx={{ display: { sm: "none", md: "inherit" }, mt: "-24px" }} size={4}></Grid>
              <Grid
                className={classes.resetFiles}
                size={{
                  md: 8,
                  sm: 6,
                  xs: 12,
                }}
                spacing={0}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!resetFiles}
                        onChange={(e) =>
                          setDSInfoState((prev) => ({
                            ...prev,
                            resetFiles: !prev.resetFiles,
                          }))
                        }
                      />
                    }
                    label="Skip previously processed files"
                  />
                </FormGroup>
              </Grid>
            </>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                required
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                value={displayName}
                error={!!displayNameError}
                helperText={displayNameError}
                onChange={(e) => updateDSInfoStateValue("displayName", e.target.value)}
                fullWidth
                label="Display Name"
                autoFocus={!inEditMode}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                required
                variant="outlined"
                error={!!nameError}
                inputProps={{ maxLength: 50 }}
                helperText={nameError}
                value={currentName}
                disabled={inEditMode && !IsDataVaultEnabled}
                onChange={(e) => handleName(e.target.value, "currentName")}
                onBlur={tableNameBlur}
                fullWidth
                label="System Name"
                inputRef={nameRef}
              />
            </Grid>
          </Grid>

          {location !== "SNOWFLAKE" ? (
            <>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={fileTypes.length ? type : ""}
                    label="Type"
                    onChange={(e) => updateDSInfoStateValue("type", e.target.value)}
                  >
                    {fileTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {type !== "parquet" && (
                <>
                  <Grid
                    size={{
                      xs: delimiter === "custom" ? 6 : 12,
                      sm: delimiter === "custom" ? 3 : 6,
                      md: delimiter === "custom" ? 2 : 4,
                    }}
                  >
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Format Delimiter</InputLabel>
                      <Select value={delimiter} label="Format Delimiter" onChange={handleDelimiter}>
                        {delimiters.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {
                    /* Added input for custom delimiter on "Custom" option selection from "Format Delimiter" for txt file support */
                    delimiter === "custom" ? (
                      <Grid
                        size={{
                          xs: delimiter === "custom" ? 6 : 12,
                          sm: delimiter === "custom" ? 3 : 6,
                          md: delimiter === "custom" ? 2 : 4,
                        }}
                      >
                        <TextField
                          required
                          label="Custom Delimeter"
                          value={customDelimeter}
                          onChange={(e) => {
                            handleCustomeDelimeter(e.target.value, "customDelimeter");
                          }}
                          inputProps={{ maxLength: 2 }}
                          sx={{ width: "100%" }}
                        />
                      </Grid>
                    ) : (
                      <></>
                    )
                  }
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasHeaderRow === "yes"}
                            onChange={(e) => updateDSInfoStateValue("hasHeaderRow", e.target.checked ? "yes" : "no")}
                          />
                        }
                        label="Has a header row"
                      />
                    </FormGroup>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Text Qualifier</InputLabel>
                      <Select
                        value={enclosedByOptions.length ? fieldEnclosedBy : ""}
                        label="Text Qualifier"
                        onChange={(e) => updateDSInfoStateValue("fieldEnclosedBy", e.target.value)}
                      >
                        {enclosedByOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              {type !== "parquet" && (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Compression</InputLabel>
                      <Select
                        value={compressionFormat}
                        label="Compression"
                        onChange={(e) => updateDSInfoStateValue("compressionFormat", e.target.value)}
                      >
                        {compressionFormats.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      md: 4,
                    }}
                  ></Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Encoding</InputLabel>
                      <Select
                        value={encodingTypes.length ? encoding : ""}
                        label="Encoding"
                        onChange={(e) => updateDSInfoStateValue("encoding", e.target.value)}
                      >
                        {encodingTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid
                style={{ display: "flex" }}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ButtonUI
                  disabled={!(location && type && path)}
                  variant="contained"
                  color="success"
                  style={{ alignSelf: "center", width: "150px" }}
                  onClick={() => setShowMapping(true)}
                >
                  <span style={{ marginTop: "2px" }}>Preview Data</span>
                </ButtonUI>
              </Grid>
              <Grid
                size={{
                  sm: 0,
                  md: 4,
                }}
              ></Grid>
              <Grid
                style={{ display: "flex" }}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Null Values</InputLabel>
                  <Select value={nullValue} label="Null Values" onChange={handleNullValues}>
                    {nullValues?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {nullValue === "custom" && (
                <Grid
                  style={{ display: "flex" }}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                  }}
                >
                  <TextField
                    required
                    label="Custom Null Values"
                    value={customNullValue}
                    onChange={(e) => handleCustomNullValue(e.target.value)}
                    sx={{ width: "100%" }}
                    helperText="Please enter the exact comma-separated value"
                  />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: ["SKIP_FILE_NUMBER", "SKIP_FILE_PERCENTAGE"].includes(onError) ? 2 : 4,
                }}
              >
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>On Error</InputLabel>
                  <Select value={onError} label="On Error" onChange={handleOnError}>
                    {errorActions.map(({ value, text }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {["SKIP_FILE_NUMBER", "SKIP_FILE_PERCENTAGE"].includes(onError) ? (
                <Grid
                  size={{
                    sm: 2,
                  }}
                >
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      InputProps={{
                        inputProps: SKIP_FILE_CUSTOM_VALUE_LIMIT[onError],
                        endAdornment:
                          onError === "SKIP_FILE_PERCENTAGE" ? (
                            <InputAdornment position="end">
                              <PercentIcon />
                            </InputAdornment>
                          ) : (
                            <></>
                          ),
                      }}
                      type="number"
                      value={onErrorNumber.toString()}
                      required
                      fullWidth
                      label="Threshold"
                      onChange={handleSkipFileValueChange}
                      helperText={skipFileError}
                      error={skipFileError !== ""}
                    />
                  </FormControl>
                </Grid>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}

          {IsDataVaultEnabled && (
            <Grid container>
              <Grid>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shareWithDC}
                        disabled={accessRole !== 1}
                        onChange={() => updateDSInfoStateValue("shareWithDC", !shareWithDC)}
                      />
                    }
                    label="Visible in Data Catalog"
                  />
                </FormGroup>
              </Grid>
              <Grid>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={excludeDQMCritical}
                        disabled={accessRole !== 1}
                        onChange={() => updateDSInfoStateValue("excludeDQMCritical", !excludeDQMCritical)}
                      />
                    }
                    label="Exclude rows with critical DQM violations"
                  />
                </FormGroup>
              </Grid>
              <Grid>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={removeMissingDataOnLoad}
                        disabled={accessRole !== 1 || !hasDVKey}
                        onChange={() => updateDSInfoStateValue("removeMissingDataOnLoad", !removeMissingDataOnLoad)}
                      />
                    }
                    label="Treat missing data as deleted"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          )}

          <Grid
            size={{
              xs: 12,
              lg: 8,
            }}
          >
            <TextField
              variant="outlined"
              value={description}
              inputProps={{ maxLength: 120 }}
              onChange={(e) => updateDSInfoStateValue("description", e.target.value)}
              fullWidth
              label="Description"
              multiline
              minRows={2}
              maxRows={2}
            />
          </Grid>
        </Grid>
        {loadTableFields.open && (
          <Confirm
            title="Import Attributes"
            description={
              <Box sx={{ width: "500px" }}>
                {loadTableFields.updateReadOnly && (
                  <Typography>Read-Only Data View does not require any additional configuration.</Typography>
                )}
                <Typography>
                  Are you sure you want to import the attributes from the selected table? This will replace the current
                  list of attributes.
                </Typography>
              </Box>
            }
            handleCancel={() => setPrevState(setLoadTableFields, { open: false })}
            handleClick={() => loadFields(loadTableFields.schemaName, loadTableFields.selectedTable, true)}
            loading={loadTableFields.loadingForConfirm}
          />
        )}
        {loadFileFields.open && (
          <Confirm
            title="Load Attributes"
            description={
              <Box>
                <Typography sx={{ marginBottom: "5px" }}>
                  Are you sure you want to load attributes from the selected file?
                </Typography>
                <Typography>
                  This will override the current list of attributes. The mappings, DQM rules and LoVs will also be
                  removed.
                </Typography>
              </Box>
            }
            handleCancel={() => setPrevState(setLoadFileFields, { open: false })}
            handleClick={() => loadFileFields.handleClick()}
          />
        )}
        {showMapping && (
          <MappingPopup
            setShowMapping={setShowMapping}
            handleSave={handleLoadFields}
            getS3FileColumns={getS3FileColumns}
          />
        )}
        {openFileBrowser &&
          (dsInfoState.connectionType.toLowerCase() === "s3" ? (
            <S3FileBrowser
              setOpenFileBrowser={setOpenFileBrowser}
              path={dsInfoState.path}
              setPath={setPath}
              stage={dsInfoState.stage}
              url={bucket}
              locationName={dsInfoState.location}
              fileSelection
            />
          ) : (
            <AzureFileBrowser
              setOpenFileBrowser={setOpenFileBrowser}
              path={`/${dsInfoState.container}${dsInfoState.path ? dsInfoState.path : "/"}`}
              container={dsInfoState.container}
              setPath={setPath}
              stage={dsInfoState.stage}
              locationName={dsInfoState.location}
              fileSelection
            />
          ))}
      </Box>
    </Box>
  );
}

export default React.memo(InfoSection);
